import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Coffee, Shirt, Construction, Sparkles } from "lucide-react";

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadedImages, setLoadedImages] = useState({});
  const [animationState, setAnimationState] = useState({});

  useEffect(() => {
    const loadProject = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Fetch project data
        const response = await fetch('/projects/projects.json');
        const data = await response.json();
        const projectData = data.projects.find(p => p.id === id);
        
        if (!projectData) {
          setError('Project not found');
          return;
        }
        
        setProject(projectData);
        
        // Load gallery images from the manifest file
        try {
          const manifestResponse = await fetch('/projects/gallery-manifests.json');
          if (manifestResponse.ok) {
            const manifest = await manifestResponse.json();
            
            // Check if this project has gallery images in the manifest
            if (manifest[id] && manifest[id].length > 0) {
              // Convert relative paths to absolute URLs
              const images = manifest[id].map(imgPath => `/projects/${imgPath}`);
              setGalleryImages(images);
            } else {
              // No gallery images found for this project
              setGalleryImages([]);
            }
          } else {
            // Manifest file not found, set empty gallery
            setGalleryImages([]);
          }
        } catch (err) {
          // Error loading manifest, set empty gallery
          console.error('Error loading gallery manifest:', err);
          setGalleryImages([]);
        }
        
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    loadProject();
  }, [id]);

  useEffect(() => {
    // Prevent right-click
    const handleContextMenu = (e) => {
      e.preventDefault();
    };

    // Prevent drag and drop
    const handleDragStart = (e) => {
      e.preventDefault();
    };

    // Prevent keyboard shortcuts
    const handleKeyDown = (e) => {
      if (
        (e.ctrlKey || e.metaKey) && 
        (e.key === 's' || e.key === 'c' || e.key === 'p' || e.key === 'u')
      ) {
        e.preventDefault();
      }
    };

    // Add event listeners
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('dragstart', handleDragStart);
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('dragstart', handleDragStart);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const handleContactClick = () => {
    navigate('/contact');
  };

  // Handle image load event
  const handleImageLoad = (index) => {
    // Set animation state to 'spiral'
    setAnimationState(prev => ({...prev, [index]: 'spiral'}));
    
    // After spiral animation completes, trigger the burst
    setTimeout(() => {
      setAnimationState(prev => ({...prev, [index]: 'burst'}));
      
      // Finally, mark the image as loaded
      setLoadedImages(prev => ({...prev, [index]: true}));
      
      // Reset animation state after burst completes
      setTimeout(() => {
        setAnimationState(prev => ({...prev, [index]: null}));
      }, 500);
    }, 500);
  };

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="container mx-auto px-4">
          <div className="animate-pulse">
            <div className="h-[400px] bg-primary/10 rounded-2xl mb-8"></div>
            <div className="h-12 bg-primary/10 rounded-lg w-1/2 mb-4"></div>
            <div className="h-6 bg-primary/5 rounded w-1/4 mb-8"></div>
            <div className="h-24 bg-primary/5 rounded-lg mb-8"></div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-64 bg-primary/5 rounded-xl"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="text-center">
          <Construction className="w-16 h-16 text-primary/50 mx-auto mb-4" />
          <h1 className="text-2xl font-semibold mb-2">Project Not Found</h1>
          <p className="text-muted-foreground mb-4">{error || 'The requested project could not be found.'}</p>
          <Link to="/projects">
            <Button>Back to Gallery</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="max-w-4xl mx-auto"
        >
          <img  
            alt={`${project.title} banner`} 
            className="w-full h-[400px] object-cover rounded-2xl mb-8"
            src={`${project.basePath}/main.webp`}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />

          <h1 className="text-4xl md:text-5xl font-bold text-gradient mb-4">
            {project.title}
          </h1>

          <div className="flex gap-4 mb-8">
            {project.merchLink ? (
              <a 
                href={project.merchLink}
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button className="flex items-center gap-2">
                  <Shirt className="w-4 h-4" />
                  Design Merch
                </Button>
              </a>
            ) : null}
            <a 
              href="https://ko-fi.com/mileroy" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="relative flex items-center gap-2 text-sm font-medium hover:text-primary transition-colors duration-300"
            >
              <Coffee className="w-4 h-4" />
              Buy me a coffee
            </a>
          </div>

          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-muted-foreground mb-8">
              {project.description}
            </p>

            {galleryImages.length > 0 ? (
              <div className="gallery-grid mb-12">
                {galleryImages.map((image, index) => (
                  <motion.div
                    key={index}
                    className="gallery-item"
                    whileHover={{ scale: 1.05 }}
                  >
                    <div className="relative overflow-hidden rounded-xl">
                      <img  
                        loading="lazy"
                        alt={`${project.title} design ${index + 1}`}
                        className={`w-full h-auto object-cover transition-all duration-500 ${
                          loadedImages[index] ? 'blur-0' : 'blur-md'
                        }`}
                        src={image}
                        onLoad={() => handleImageLoad(index)}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      
                      {/* Loading spinner */}
                      {!loadedImages[index] && (
                        <div className="absolute inset-0 flex items-center justify-center bg-primary/5">
                          <div className="relative w-12 h-12">
                            <div className="absolute inset-0 rounded-full border-4 border-primary/10"></div>
                            <div className="absolute inset-0 rounded-full border-4 border-t-4 border-t-primary border-r-primary/50 border-b-transparent border-l-transparent animate-spin"></div>
                            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-purple-500/20 via-pink-500/20 to-blue-500/20 animate-pulse"></div>
                          </div>
                        </div>
                      )}
                      
                      {/* Animation effects */}
                      <AnimatePresence>
                        {animationState[index] === 'spiral' && (
                          <motion.div 
                            className="absolute inset-0 flex items-center justify-center pointer-events-none"
                            initial={{ opacity: 1, scale: 1 }}
                            animate={{ 
                              opacity: 0, 
                              scale: 0.1,
                              rotate: 360
                            }}
                            exit={{ opacity: 0 }}
                            transition={{ 
                              duration: 0.5,
                              ease: "easeInOut"
                            }}
                          >
                            <div className="w-12 h-12 rounded-full bg-gradient-to-r from-purple-500 via-pink-500 to-blue-500"></div>
                          </motion.div>
                        )}
                        
                        {animationState[index] === 'burst' && (
                          <motion.div 
                            className="absolute inset-0 flex items-center justify-center pointer-events-none"
                            initial={{ opacity: 1, scale: 0.1 }}
                            animate={{ opacity: 0, scale: 2 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.5 }}
                          >
                            <div className="w-16 h-16 rounded-full bg-gradient-to-r from-purple-500 via-pink-500 to-blue-500 opacity-30"></div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-primary/5 rounded-xl mb-12">
                <p className="text-muted-foreground">No gallery images available for this project yet.</p>
              </div>
            )}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="mt-16 md:mt-24"
          >
            <div className="glass-card p-6 md:p-8">
              <div className="text-center space-y-8 md:space-y-10">
                <div className="space-y-6">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10">
                    <Sparkles className="w-6 h-6 text-primary" />
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-gradient">
                    Let&apos;s Create Something Together
                  </h2>
                  <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
                    Have a project in mind? Whether it&apos;s a custom illustration, digital art piece, 
                    or a creative collaboration, I&apos;m here to bring your vision to life.
                  </p>
                </div>
                
                <div className="pt-4">
                  <Button 
                    size="lg" 
                    className="bg-gradient-to-r from-purple-600 via-pink-500 to-blue-500 text-white hover:opacity-90 transition-opacity px-8 py-6 text-base md:text-lg"
                    onClick={handleContactClick}
                  >
                    Start a Project
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default ProjectDetail;
